import { useEffect, useRef } from 'react';

export function usePageEffects(pathname) {
  const initialRoute = useRef(true);
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const observedTargets = new WeakSet();

    if (reduceMotion || !('IntersectionObserver' in window)) {
      const revealAll = () => document.querySelectorAll('[data-reveal]').forEach((target) => target.classList.add('is-visible'));
      revealAll();
      const mutations = new MutationObserver(revealAll);
      mutations.observe(document.body, { childList: true, subtree: true });
      return () => mutations.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -7%' },
    );

    let revealFrame = 0;
    const revealVisibleTargets = () => {
      if (revealFrame) return;
      revealFrame = window.requestAnimationFrame(() => {
        document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((target) => {
          const bounds = target.getBoundingClientRect();
          if (bounds.bottom < 0 || bounds.top > window.innerHeight * 0.93) return;
          target.classList.add('is-visible');
          observer.unobserve(target);
        });
        revealFrame = 0;
      });
    };

    const registerTargets = () => {
      document.querySelectorAll('[data-reveal]').forEach((target) => {
        if (observedTargets.has(target)) return;
        observedTargets.add(target);
        observer.observe(target);
      });
      revealVisibleTargets();
    };

    registerTargets();
    const mutations = new MutationObserver(registerTargets);
    mutations.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', revealVisibleTargets, { passive: true });
    window.addEventListener('resize', revealVisibleTargets);

    return () => {
      mutations.disconnect();
      observer.disconnect();
      window.cancelAnimationFrame(revealFrame);
      window.removeEventListener('scroll', revealVisibleTargets);
      window.removeEventListener('resize', revealVisibleTargets);
    };
  }, [pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  useEffect(() => {
    if (initialRoute.current) {
      initialRoute.current = false;
      return undefined;
    }

    const focusMain = () => {
      const main = document.querySelector('#main-content');
      if (!main) return false;
      main.tabIndex = -1;
      main.focus({ preventScroll: true });
      return true;
    };

    if (focusMain()) return undefined;
    const mutations = new MutationObserver(() => {
      if (!focusMain()) return;
      mutations.disconnect();
    });
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => mutations.disconnect();
  }, [pathname]);
}
